import {
  type CohortBuilderDefinitionLegacy,
  CohortBuilderItemChangedBy,
  CohortBuilderItemChangeStatusLegacy,
  type DerivedEventLegacy,
  IndexDateTypeLegacy,
  type IndexDateVariableLegacy,
} from '@verantos-dev/types-cohorts';

import { computeDiffForCohortBuilderItem } from '../computeDiffForCohortBuilderItem';
import { getNextDerivedEventsForDerivedEventNameChange } from '../getNextDerivedEventsForDerivedEventNameChange';
import { getNextIndexDateForEventNameChange } from '../getNextIndexDateForEventNameChange';
import { getNextSubjectVariablesForDerivedEventNameChange } from '../getNextSubjectVariablesForDerivedEventNameChange';

export const getFullCohortDslForDerivedEvent = (
  currentCohortDefinition: CohortBuilderDefinitionLegacy | undefined,
  nextDerivedEvent: DerivedEventLegacy,
  addChangeStatus = false
) => {
  const currentSubjectVariables = currentCohortDefinition?.subjectVariables ?? [];
  const currentIndexDate = currentCohortDefinition?.indexDate;
  const currentDerivedEvents = currentCohortDefinition?.derivedEvents ?? [];
  const payloadDerivedEventIndex = currentDerivedEvents.findIndex(
    (derivedEvents) => derivedEvents.id === nextDerivedEvent.id
  );

  // check if the name of the simple event has changed, to determine if we need to update the usages of this name
  // in items that reference this simple event.
  const isDerivedEventNameChanged =
    payloadDerivedEventIndex > -1 &&
    currentDerivedEvents[payloadDerivedEventIndex].name !== nextDerivedEvent.name;

  const nextSubjectVariables = isDerivedEventNameChanged
    ? getNextSubjectVariablesForDerivedEventNameChange(
        currentSubjectVariables,
        currentDerivedEvents[payloadDerivedEventIndex].name,
        nextDerivedEvent.name,
        nextDerivedEvent.type
      )
    : currentSubjectVariables;

  let nextIndexDate: IndexDateVariableLegacy | undefined = currentIndexDate;
  if (
    isDerivedEventNameChanged &&
    currentIndexDate &&
    currentIndexDate.type === IndexDateTypeLegacy.EVENT
  ) {
    nextIndexDate = getNextIndexDateForEventNameChange(
      currentIndexDate,
      currentDerivedEvents[payloadDerivedEventIndex].name,
      nextDerivedEvent.name,
      nextDerivedEvent.type,
      false
    );
  }

  const nameChangedDerivedEvents = isDerivedEventNameChanged
    ? getNextDerivedEventsForDerivedEventNameChange(
        currentDerivedEvents,
        currentDerivedEvents[payloadDerivedEventIndex].name,
        nextDerivedEvent.name,
        nextDerivedEvent.type
      )
    : currentDerivedEvents;

  // If the derived event is already defined in state, replace it with the new one
  // Otherwise, add the new derived event to the end of the array
  const derivedEvents =
    payloadDerivedEventIndex > -1
      ? nameChangedDerivedEvents.map((derivedEvent) => {
          if (derivedEvent.id === nextDerivedEvent.id) {
            if (addChangeStatus) {
              let _changeStatus: CohortBuilderItemChangeStatusLegacy | undefined;
              if (computeDiffForCohortBuilderItem(derivedEvent, nextDerivedEvent)) {
                // if derived event previously existed and is different from current update
                if (derivedEvent.changeStatus === CohortBuilderItemChangeStatusLegacy.NEW) {
                  // if derived event was new, then it's still new
                  _changeStatus = CohortBuilderItemChangeStatusLegacy.NEW;
                } else {
                  // derived event is edited
                  _changeStatus = CohortBuilderItemChangeStatusLegacy.EDITED;
                }
              } else {
                // if derived event previously existed and is the same as current update, then it's unchanged
                _changeStatus = derivedEvent.changeStatus;
              }

              return {
                ...nextDerivedEvent,
                changeStatus: _changeStatus,
                changedBy: CohortBuilderItemChangedBy.COHORT_BUILDER,
              };
            } else {
              return nextDerivedEvent;
            }
          }
          return derivedEvent;
        })
      : [
          ...nameChangedDerivedEvents,
          addChangeStatus
            ? {
                ...nextDerivedEvent,
                changeStatus: CohortBuilderItemChangeStatusLegacy.NEW,
                changedBy: CohortBuilderItemChangedBy.COHORT_BUILDER,
              }
            : nextDerivedEvent,
        ];

  const cohortDefinition: CohortBuilderDefinitionLegacy = {
    studyPeriodRestrictions: currentCohortDefinition?.studyPeriodRestrictions,
    subjectVariables: nextSubjectVariables,
    indexDate: nextIndexDate,
    simpleEvents: currentCohortDefinition?.simpleEvents ?? [],
    derivedEvents,
    // FEATURE FLAG: Preserve analytical variables fields when present
    // These fields come from backend when analytical_variables_in_cohort_builder is enabled
    // CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag, ensure these fields are always preserved
    analyticalVariables: currentCohortDefinition?.analyticalVariables,
    criteria: currentCohortDefinition?.criteria,
  };

  return {
    cohortDefinition,
  };
};

export const getFullCohortDslForDeleteDerivedEvent = (
  currentCohortDefinition: CohortBuilderDefinitionLegacy | undefined,
  derivedEventId: string
) => {
  const currentDerivedEvents = currentCohortDefinition?.derivedEvents ?? [];
  const payloadDerivedEventIndex = currentDerivedEvents.findIndex(
    (derivedEvent) => derivedEvent.id === derivedEventId
  );

  const cohortDefinition: CohortBuilderDefinitionLegacy = {
    studyPeriodRestrictions: currentCohortDefinition?.studyPeriodRestrictions,
    indexDate: currentCohortDefinition?.indexDate,
    subjectVariables: currentCohortDefinition?.subjectVariables ?? [],
    simpleEvents: currentCohortDefinition?.simpleEvents ?? [],
    derivedEvents: [
      ...currentDerivedEvents.slice(0, payloadDerivedEventIndex),
      ...currentDerivedEvents.slice(payloadDerivedEventIndex + 1),
    ],
    // FEATURE FLAG: Preserve analytical variables fields when present
    // These fields come from backend when analytical_variables_in_cohort_builder is enabled
    // CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag, ensure these fields are always preserved
    analyticalVariables: currentCohortDefinition?.analyticalVariables,
    criteria: currentCohortDefinition?.criteria,
  };

  return {
    cohortDefinition,
  };
};
